import {
  ApplicantType,
  DISCLOSURES,
  ExecutionModes,
  VerificationNames
} from "shared/types";
import { combineVerifications } from "shared/utils/combine-verifications";
import {
  VerificationSchema,
  VerificationSettings
} from "types/landlord/verificationTemplate";
import { useEffect } from "react";
import { useTemplateContext } from "../../provider";
import { DataVendors } from "shared/types";
import { ServiceCard } from "../../components/ServiceCard";
import { Radio } from "shared/components/forms/Radio";
import { Show } from "shared/components";
import { schemasWithConfigure, sideImageURL } from "./constants";
import { getLogoSrc } from "../../../../../utils/getLogoSrc";
import { cx } from "../../../../../utils/classnames";
import { VerificationSchemaView } from "../types";
import { VerticalSpacer } from "../../components/SideBar/VerticalSpacer";
import { usePostHog } from "posthog-js/react";
import {
  buildVerificationSchemaData,
  getValueByPath,
  removeValueByPath
} from "../../utils";
import { housingHistoryFallback } from "../../constant";

export const VerificationsSlice = ({
  verificationSchemas
}: {
  verificationSchemas: VerificationSchema[];
}) => {
  const { featureFlags } = usePostHog();

  const {
    template,
    setTemplate,
    partnerType,
    readOnly,
    setSelectedVerificationSchema,
    createMode,
    verifiedIncome,
    setVerifiedIncome
  } = useTemplateContext();

  useEffect(() => {
    if (!template?.id) {
      const evictionReport =
        template?.verification_schemas[partnerType][
          VerificationNames.EvictionReport
        ];

      if (createMode && evictionReport && !evictionReport.vendor)
        handleDataVendorChange(
          DataVendors.Cleara,
          VerificationNames.EvictionReport
        );
      return;
    }

    const reports = [
      VerificationNames.CriminalReport,
      VerificationNames.EvictionReport
    ];

    reports.forEach((report) => {
      const reportData = template.verification_schemas[partnerType][
        report
      ] as VerificationSettings;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!reportData || reportData.vendor) return;

      handleDataVendorChange(DataVendors.CIC, report);
    });
  }, [template]);

  const handleDataVendorChange = (
    vendor: DataVendors,
    schemaName: VerificationNames
  ) => {
    if (!template) return;

    const updatedTemplate = {
      ...template,
      verification_schemas: {
        ...template.verification_schemas,
        [partnerType]: {
          ...template.verification_schemas[partnerType],
          [schemaName]: {
            ...template.verification_schemas[partnerType][schemaName],
            vendor: vendor
          }
        }
      }
    };
    setTemplate(updatedTemplate);
  };

  const updateExecutionMode = (
    execution_mode: ExecutionModes,
    schemaName: VerificationNames
  ) => {
    if (!template) return;

    const updatedTemplate = {
      ...template,
      verification_schemas: {
        ...template.verification_schemas,
        [partnerType]: {
          ...template.verification_schemas[partnerType],
          [schemaName]: {
            ...template.verification_schemas[partnerType][schemaName],
            execution_mode: execution_mode
          }
        }
      }
    };
    setTemplate(updatedTemplate);
  };

  const toggleIncomeVerification = (toggle: boolean) => {
    if (!template) return;

    const basePath = ["verification_schemas", partnerType];

    const updatedTemplate = buildVerificationSchemaData(
      template,
      [
        ...basePath,
        VerificationNames.IncomeVerification,
        VerificationNames.IncomeVerification + "_step_enabled"
      ],
      toggle
    );

    const finalTemplate = toggle
      ? buildVerificationSchemaData(
          updatedTemplate,
          [...basePath, VerificationNames.VerifiedIncomeVerification],
          verifiedIncome
        )
      : removeValueByPath(updatedTemplate, [
          ...basePath,
          VerificationNames.VerifiedIncomeVerification
        ]);

    setVerifiedIncome(
      getValueByPath(template, [
        ...basePath,
        VerificationNames.VerifiedIncomeVerification
      ])
    );

    setTemplate(finalTemplate);
  };

  const toggleSwitch = (
    checked: boolean,
    serviceName: VerificationNames,
    partnerType: ApplicantType
  ) => {
    if (!template) return;

    const housingHistoryRequiredFieldsMergedWithDefaults = () => {
      const configurationField = [
        "rent_option_required",
        "own_home_option_required",
        "other_option_required"
      ];

      const rootConfig =
        template.verification_schemas[partnerType][
          VerificationNames.HousingHistory
        ];

      const ret = configurationField.reduce((acc, field) => {
        const fieldData = rootConfig?.[field];

        if (!fieldData)
          return {
            ...acc,
            [field]: housingHistoryFallback[field]
          };

        return {
          ...acc,
          [field]: {
            ...housingHistoryFallback[field],
            ...fieldData
          }
        };
      }, {});

      return {
        ...rootConfig,
        ...ret
      };
    };

    if (!checked) {
      const { [serviceName]: _, ...remainingServices } =
        template.verification_schemas[partnerType];

      if (serviceName === VerificationNames.HousingHistory) {
        const updatedTemplate = {
          ...template,
          verification_schemas: {
            ...template.verification_schemas,
            [partnerType]: {
              ...template.verification_schemas[partnerType],
              [VerificationNames.RentalVerification]: undefined,
              [serviceName]: {
                ...housingHistoryRequiredFieldsMergedWithDefaults(),
                address_step_enabled: false
              }
            }
          }
        };
        setTemplate(updatedTemplate);
      } else {
        const updatedTemplate = {
          ...template,
          verification_schemas: {
            ...template.verification_schemas,
            [partnerType]: remainingServices
          }
        };
        setTemplate(updatedTemplate);
      }
    } else {
      const updatedTemplate = {
        ...template,
        verification_schemas: {
          ...template.verification_schemas,
          [partnerType]: {
            ...template.verification_schemas[partnerType],
            [serviceName]: [
              VerificationNames.CriminalReport,
              VerificationNames.EvictionReport
            ].includes(serviceName)
              ? { vendor: DataVendors.Cleara }
              : // Add default state if housing history is empty for old templates
                serviceName === VerificationNames.HousingHistory
                ? {
                    ...housingHistoryRequiredFieldsMergedWithDefaults(),
                    address_step_enabled: true
                  }
                : {}
          }
        }
      };

      setTemplate(updatedTemplate);
    }
  };

  if (!template) return null;

  const filteredVerifications = verificationSchemas.filter(
    (v) => !DISCLOSURES.includes(v.name)
  );

  const servicesWithoutRentalVerification = filteredVerifications.filter(
    (v) => v.name !== VerificationNames.RentalVerification
  );

  const rentalVerification = filteredVerifications.find(
    (v) => v.name === VerificationNames.RentalVerification
  );

  const hasLandlordSurvey = Boolean(
    featureFlags.isFeatureEnabled("landlord_survey")
  );

  const services = combineVerifications<VerificationSchema>(
    servicesWithoutRentalVerification,
    [
      [
        VerificationNames.IncomeVerification,
        VerificationNames.VerifiedIncomeVerification
      ]
    ]
  );

  return (
    <div className="space-y-5 animate-appear pb-6" key={partnerType}>
      {services.map((schema: VerificationSchema | VerificationSchema[]) => {
        if (Array.isArray(schema))
          return (
            <ServiceCard
              key={schema[1].name}
              title="Income history"
              description="Get information from applicants about their income history and verify it. Available verification methods: bank connection, payroll provider connection, or documents"
              sideImageURL={sideImageURL[schema[1].name]}
              onButtonClick={() => {
                setSelectedVerificationSchema(
                  VerificationSchemaView.BankVerification
                );
              }}
              readonly={readOnly}
              verificationSchema={schema[1]}
              fee={schema[1].screening_fee}
              onToggleChange={toggleIncomeVerification}
              toggleEnabled={
                Boolean(template.verification_schemas) &&
                Boolean(
                  (
                    template.verification_schemas[partnerType][
                      VerificationNames.IncomeVerification
                    ] as VerificationNames | undefined
                  )?.[VerificationNames.IncomeVerification + "_step_enabled"] ??
                    true
                ) &&
                Boolean(
                  template.verification_schemas[partnerType][
                    VerificationNames.VerifiedIncomeVerification
                  ]
                )
              }
            />
          );
        else if (schema.name === VerificationNames.HousingHistory)
          return (
            <div key={schema.name}>
              <ServiceCard
                updateExecutionMode={updateExecutionMode}
                onButtonClick={
                  schemasWithConfigure.includes(schema.name)
                    ? () => {
                        setSelectedVerificationSchema(
                          VerificationSchemaView.RentVerification
                        );
                      }
                    : undefined
                }
                title={schema.title}
                description={schema.description}
                sideImageURL={sideImageURL[schema.name]}
                readonly={readOnly}
                verificationSchema={schema}
                fee={schema.screening_fee}
                onToggleChange={(checked) =>
                  toggleSwitch(checked, schema.name, partnerType)
                }
                toggleEnabled={
                  schema.required
                    ? true
                    : Boolean(template.verification_schemas) &&
                      Boolean(
                        template.verification_schemas[partnerType][schema.name]
                          ?.address_step_enabled
                      )
                }
              />
              {rentalVerification && hasLandlordSurvey && (
                <div className="h-[188px] flex items-center">
                  <VerticalSpacer
                    isLast={true}
                    isActive={false}
                    height={94}
                    className="ml-[112px] hidden md:block"
                    roundClassName="h-[9px] w-[9px] hidden md:block"
                    horizontalLineClassName="w-2 hidden md:block"
                  />
                  <div className="max-h-[156px] pl-3">
                    <ServiceCard
                      hideFooter={true}
                      cardClassName="p-2 ml-4 max-w-[750px]"
                      imageClassName="max-h-[156px] w-[115px]"
                      onButtonClick={
                        schemasWithConfigure.includes(rentalVerification.name)
                          ? () =>
                              setSelectedVerificationSchema(
                                rentalVerification.name as unknown as VerificationSchemaView
                              )
                          : undefined
                      }
                      title={rentalVerification.title}
                      description={rentalVerification.description}
                      sideImageURL={sideImageURL[rentalVerification.name]}
                      verificationSchema={rentalVerification}
                      fee={rentalVerification.screening_fee}
                      onToggleChange={(v) => {
                        // Allow to toggle switch only if housing history is enabled
                        if (
                          template.verification_schemas[partnerType][
                            VerificationNames.HousingHistory
                          ]?.address_step_enabled
                        )
                          toggleSwitch(v, rentalVerification.name, partnerType);
                      }}
                      readonly={
                        !Boolean(
                          template.verification_schemas[partnerType][
                            VerificationNames.HousingHistory
                          ]?.address_step_enabled
                        )
                      }
                      toggleEnabled={Boolean(
                        template.verification_schemas[partnerType][
                          rentalVerification.name
                        ]
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        else
          return (
            <div key={schema.name}>
              <ServiceCard
                updateExecutionMode={updateExecutionMode}
                onButtonClick={
                  schemasWithConfigure.includes(schema.name)
                    ? () => {
                        let selectedSchema: VerificationSchemaView;

                        if (schema.name === VerificationNames.RentalApplication)
                          selectedSchema =
                            VerificationSchemaView.PersonalDetails;
                        else if (
                          schema.name === VerificationNames.HousingHistory
                        )
                          selectedSchema =
                            VerificationSchemaView.RentVerification;
                        else
                          selectedSchema =
                            schema.name as unknown as VerificationSchemaView;

                        setSelectedVerificationSchema(selectedSchema);
                      }
                    : undefined
                }
                title={
                  schema.name === VerificationNames.RentalApplication
                    ? "Basic information "
                    : schema.title
                }
                description={schema.description}
                sideImageURL={sideImageURL[schema.name]}
                readonly={readOnly}
                verificationSchema={schema}
                fee={schema.screening_fee}
                onToggleChange={
                  [
                    VerificationNames.IdentityVerification,
                    VerificationNames.RentalApplication
                  ].includes(schema.name)
                    ? undefined
                    : (v) => toggleSwitch(v, schema.name, partnerType)
                }
                toggleEnabled={
                  schema.required
                    ? true
                    : Boolean(template.verification_schemas) &&
                      Boolean(
                        template.verification_schemas[partnerType][schema.name]
                      )
                }
              >
                <Show
                  if={[
                    VerificationNames.CriminalReport,
                    VerificationNames.EvictionReport
                  ].includes(schema.name)}
                >
                  {[DataVendors.Cleara, DataVendors.CIC].map((vendor) => {
                    const disabled = !Boolean(
                      template.verification_schemas[partnerType][schema.name]
                    );
                    return (
                      <div
                        key={vendor}
                        className={cx("relative", disabled ? "opacity-40" : "")}
                      >
                        <Radio
                          id={vendor}
                          name="vendor"
                          label={
                            <img
                              className="h-5"
                              src={getLogoSrc(vendor)}
                              alt={vendor + "logo"}
                            />
                          }
                          checked={
                            Boolean(
                              template.verification_schemas[partnerType][
                                schema.name
                              ]
                            ) &&
                            (
                              template.verification_schemas[partnerType][
                                schema.name
                              ] as VerificationSettings | undefined
                            )?.vendor === vendor
                          }
                          onChange={() =>
                            handleDataVendorChange(vendor, schema.name)
                          }
                          disabled={disabled}
                        />
                      </div>
                    );
                  })}
                </Show>
              </ServiceCard>
            </div>
          );
      })}
    </div>
  );
};
